/**
 * Таблица «ключ встроенного ↔ поле анализа» — одна на все читатели (#2490,
 * план `docs/plans/2026-10-18-builtin-criteria-e2.md`, решение 2). Запасная
 * ветка Э2: запись, у которой нет ответа встроенного (анализ до Э1,
 * пропущенный или упавший шаг, реплей), читается по старому полю анализа.
 *
 * Правила — скрипта сверки тени Э1 (`builtin-shadow-compare.ts`, удалён с Э3,
 * #2846; он считал исходы этой же функцией): обе стороны приводятся к вопросу «событие было»,
 * у конфиденциальности полярность обратная, мат — только реплики сотрудника.
 * Отличие от скрипта до Э2 одно: `compliance.recordingDisclaimer` с
 * `applicable === false` — «не применимо», а не откат к
 * `recordingDisclaimer.mentioned` (карточка Compliance Results такую запись не
 * считает, а скрипт считал бы «не пройдено»).
 */
import type { MandatoryPhraseCode } from '../schemas/prompt-builder';
import type { BuiltinCriterionKey } from './builtin-catalog';

/**
 * Исход по старому полю:
 * - `happened` / `not_happened` — событие встроенного было / не было;
 * - `not_applicable` — анализ сказал, что проверка к звонку не применима;
 * - `not_checked` — поля нет (тумблер выключен, анализ без блока);
 * - `old_shape` — блок есть, но в форме, по которой ответа не вывести:
 *   фразы без `missingCodes`, мат без `instances`;
 * - `no_legacy` — у встроенного старого поля нет (`satisfaction_guarantee`;
 *   `forbidden_words` — старое поле удалено вместе с ним, а на проде за всю
 *   историю им не заполнен ни один анализ, #2784).
 *
 * Результат дают только первые три; `not_checked`, `old_shape` и `no_legacy`
 * потребитель читает как «нет результата».
 */
export type LegacyBuiltinOutcomeKind =
  | 'happened'
  | 'not_happened'
  | 'not_applicable'
  | 'not_checked'
  | 'old_shape'
  | 'no_legacy';

/**
 * Откуда исход уведомления о записи: `compliance` — из
 * `compliance.recordingDisclaimer` (его считает карточка Compliance Results),
 * `mentioned` — из `recordingDisclaimer.mentioned`, только когда решающего
 * блока `compliance` нет.
 */
export type LegacyBuiltinBasis = 'compliance' | 'mentioned';

export interface LegacyBuiltinOutcome {
  outcome: LegacyBuiltinOutcomeKind;
  /** Только у `recording_disclosed` с исходом из поля; иначе `null`. */
  basis: LegacyBuiltinBasis | null;
}

/** Коды фраз `mandatoryPhrases.missingCodes`, которые читают встроенные. */
export const LEGACY_PHRASE_CODES = {
  company_named: 'COMPANY_NAME',
  callback_offered: 'CALLBACK_OPTION',
} as const;

/**
 * Код строки обязательных фраз (`MANDATORY_PHRASE_CODES`) → встроенный, чей
 * вопрос он повторяет (панель звонка и PDF, #2518; план Э2, решение 8).
 * Строка одна на несколько ключей, поэтому у организации с Э2 она
 * фильтруется по кодам: остаются коды ключей, чей результат на записи —
 * старое поле. Уведомление о записи старым полем читается из `compliance`, а
 * гарантия удовлетворённости старого поля не имеет (`no_legacy`), но код в
 * строке повторяет и их: на записи с ответом встроенного он стоял бы рядом с
 * его строкой в группе «Критерии», а на анализе до Э1 пропал бы без замены.
 */
export const PHRASE_CODE_BUILTIN_KEYS = {
  COMPANY_NAME: 'company_named',
  CALLBACK_OPTION: 'callback_offered',
  RECORDING_DISCLAIMER: 'recording_disclosed',
  SATISFACTION_GUARANTEE: 'satisfaction_guarantee',
} as const satisfies Record<MandatoryPhraseCode, BuiltinCriterionKey>;

function objectOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function boolOf(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

const plain = (outcome: LegacyBuiltinOutcomeKind): LegacyBuiltinOutcome => ({
  outcome,
  basis: null,
});

const happened = (value: boolean | null): LegacyBuiltinOutcomeKind =>
  value === null ? 'not_checked' : value ? 'happened' : 'not_happened';

/** Фраза: `missingCodes` не содержит код — событие было. */
function phrase(so: Record<string, unknown>, code: string): LegacyBuiltinOutcome {
  const block = objectOf(so.mandatoryPhrases);
  if (!block) return plain('not_checked');
  const missing = block.missingCodes;
  if (!Array.isArray(missing)) return plain('old_shape');
  return plain(missing.includes(code) ? 'not_happened' : 'happened');
}

/**
 * Проверка блока `compliance`: применима — `passed` (с полярностью),
 * `applicable === false` — «не применимо», иначе — «не проверял».
 */
function complianceCheck(
  so: Record<string, unknown>,
  name: 'tcpaCompliance' | 'dataPrivacy',
  happenedWhenPassed: boolean
): LegacyBuiltinOutcome {
  const check = objectOf(objectOf(so.compliance)?.[name]);
  const applicable = boolOf(check?.applicable);
  if (applicable === false) return plain('not_applicable');
  if (applicable !== true) return plain('not_checked');
  const passed = boolOf(check?.passed);
  return plain(happened(passed === null ? null : passed === happenedWhenPassed));
}

/**
 * Решает блок `compliance.recordingDisclaimer` с булевым `applicable`; без
 * такого блока — `recordingDisclaimer.mentioned`.
 */
function recordingDisclaimer(so: Record<string, unknown>): LegacyBuiltinOutcome {
  const check = objectOf(objectOf(so.compliance)?.recordingDisclaimer);
  const applicable = boolOf(check?.applicable);
  if (applicable === false) return { outcome: 'not_applicable', basis: 'compliance' };
  if (applicable === true) {
    const outcome = happened(boolOf(check?.passed));
    return { outcome, basis: outcome === 'not_checked' ? null : 'compliance' };
  }
  const mentioned = boolOf(objectOf(so.recordingDisclaimer)?.mentioned);
  return mentioned === null
    ? plain('not_checked')
    : { outcome: happened(mentioned), basis: 'mentioned' };
}

/** Мат сотрудника: реплика с `speaker = 'agent'` в `instances`. */
function agentProfanity(so: Record<string, unknown>): LegacyBuiltinOutcome {
  const block = objectOf(so.profanityDetection);
  if (!block) return plain('not_checked');
  // Блок есть, а списка реплик нет — форма старая: «нет» тут была бы выдумкой.
  if (!Array.isArray(block.instances)) return plain('old_shape');
  const agent = block.instances.some(
    (instance) => objectOf(instance)?.speaker === 'agent'
  );
  return plain(agent ? 'happened' : 'not_happened');
}

/**
 * Исход встроенного `key` по старому полю анализа. `structuredOutput` —
 * `analyses.result_json -> 'structuredOutput'` текущего анализа записи (или
 * его проекция без текста клиента, `LEGACY_BUILTIN_SKELETON_SQL` в
 * `@aica/database`); не объект — «не проверял».
 */
export function legacyBuiltinOutcome(
  key: BuiltinCriterionKey,
  structuredOutput: unknown
): LegacyBuiltinOutcome {
  if (key === 'satisfaction_guarantee' || key === 'forbidden_words') {
    return plain('no_legacy');
  }
  const so = objectOf(structuredOutput);
  if (!so) return plain('not_checked');
  switch (key) {
    case 'company_named':
    case 'callback_offered':
      return phrase(so, LEGACY_PHRASE_CODES[key]);
    case 'recording_disclosed':
      return recordingDisclaimer(so);
    case 'agent_profanity':
      return agentProfanity(so);
    case 'tcpa_compliant':
      return complianceCheck(so, 'tcpaCompliance', true);
    case 'data_privacy_violation':
      // Полярность обратная: нарушение было — это `passed = false`.
      return complianceCheck(so, 'dataPrivacy', false);
  }
}
